import { useCallback, useEffect, useMemo } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import * as Sentry from '@sentry/react';

import { STORAGE_KEYS } from 'ae-constants/constants';

import { logJwtClaim } from '../jwt-debug';
import { AuthContext } from '../rtk-auth-provider';

interface AuthContentRootProps {
  logoutURL?: string;
}

export function AuthContentRoot({
  children,
  logoutURL,
}: React.PropsWithChildren<AuthContentRootProps>) {
  const {
    getAccessTokenSilently,
    loginWithRedirect,
    logout: auth0Logout,
    isAuthenticated,
    error,
  } = useAuth0();

  // TODO: Remove the app specific code from this file.
  useEffect(() => {
    if (!isAuthenticated) {
      window.localStorage.removeItem(STORAGE_KEYS.AWN_ORGANIZATION);
    }
    if (error) {
      Sentry.captureException(error, { tags: { awth: 'sdkError' } });
    }
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        logJwtClaim(token);
      } catch {
        // do nothing
      }
    })();
  }, [getAccessTokenSilently, isAuthenticated, error]);

  const logout = useCallback(async () => {
    window.localStorage.removeItem(STORAGE_KEYS.AWN_ORGANIZATION);
    try {
      await auth0Logout({
        logoutParams: { returnTo: logoutURL || window.location.origin },
      });
    } catch (logoutErr) {
      Sentry.captureException(logoutErr, { tags: { awth: 'logoutError' } });
    }
  }, [auth0Logout, logoutURL]);

  const fetchApi: WindowOrWorkerGlobalScope['fetch'] = useCallback(
    async (url, options) => {
      let token;
      try {
        token = await getAccessTokenSilently();
      } catch (e) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        // The error should be of type [OAuthError](https://auth0.github.io/auth0-react/classes/errors.oautherror.html), which has the field `error`.
        // But due to the way Auth0 SDK is compiled in their production build, the prototype ends up being the generic Error.
        if (e?.error === 'login_required') {
          await loginWithRedirect();
        } else {
          Sentry.captureException(e, { tags: { awth: 'getTokenSilently' } });
        }

        // Stop the function execution to before actual requests are sent.
        throw e;
      }

      const headers = new Headers(options?.headers);
      headers.set('Authorization', `Bearer ${token}`);

      return window.fetch(url, { ...options, headers });
    },
    [getAccessTokenSilently, loginWithRedirect],
  );

  const value = useMemo(() => ({ fetchApi, logout }), [fetchApi, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
